/* ==========================================================================
   ff-home.js — behaviours for the UNITE Pro homepage redesign sections.

   Vanilla JS, no jQuery/Swiper dependency. Everything is initialised from
   data attributes so sections can be added/removed in the theme editor
   (re-inits on shopify:section:load).

     [data-ff-scroller]      horizontal scroll-snap track with arrows +
                             progress bar (best sellers, categories, …)
     [data-ff-video-open]    opens the full-screen video modal
                             (data-video-type = youtube | vimeo | mp4)
     [data-ff-autoplay]      muted preview <video> — plays only while in view
     [data-ff-expand]        "read the full letter" toggle
     .ff-reveal              entrance reveal on scroll
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------- */
  /* Reveal on scroll                                                        */
  /* ---------------------------------------------------------------------- */
  var revealObserver = null;
  function initReveals(root) {
    var els = (root || document).querySelectorAll('.ff-reveal:not(.is-in)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    els.forEach(function (el) { revealObserver.observe(el); });
  }

  /* ---------------------------------------------------------------------- */
  /* Scroll-snap track                                                       */
  /* ---------------------------------------------------------------------- */
  function initScroller(el) {
    if (el.ffScrollerInit) return;
    el.ffScrollerInit = true;

    var track = el.querySelector('.ff-track');
    if (!track) return;
    var prev = el.querySelector('[data-ff-prev]');
    var next = el.querySelector('[data-ff-next]');
    var bar = el.querySelector('.ff-progress__bar');

    function step() {
      var first = track.firstElementChild;
      if (!first) return track.clientWidth * 0.8;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      var slide = first.getBoundingClientRect().width + gap;
      var perView = Math.max(1, Math.floor(track.clientWidth / slide));
      return slide * perView;
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var x = track.scrollLeft;
      if (prev) prev.disabled = x <= 2;
      if (next) next.disabled = x >= max - 2;
      if (bar) {
        var visible = max <= 0 ? 1 : track.clientWidth / track.scrollWidth;
        var w = Math.max(visible * 100, 8);
        bar.style.width = w + '%';
        var travel = 100 - w;
        var pct = max <= 0 ? 0 : (x / max);
        bar.style.transform = 'translateX(' + (pct * travel / w * 100) + '%)';
      }
      el.classList.toggle('ff-scroller--no-overflow', max <= 2);
    }

    if (prev) prev.addEventListener('click', function (e) { e.preventDefault(); track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
    if (next) next.addEventListener('click', function (e) { e.preventDefault(); track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      setTimeout(function () { update(); ticking = false; }, 40);
    }, { passive: true });
    window.addEventListener('resize', update);
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(track);
    }
    // keyboard: arrows when track focused
    track.setAttribute('tabindex', track.getAttribute('tabindex') || '0');
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); track.scrollBy({ left: step(), behavior: 'smooth' }); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); track.scrollBy({ left: -step(), behavior: 'smooth' }); }
    });
    update();
    // images load later and change widths
    setTimeout(update, 600);
    window.addEventListener('load', update);
  }

  /* ---------------------------------------------------------------------- */
  /* Muted preview videos: play only while visible                           */
  /* ---------------------------------------------------------------------- */
  var autoplayObserver = null;
  function initAutoplay(video) {
    if (video.ffAutoplayInit) return;
    video.ffAutoplayInit = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    // clip loop: only play [start, end) of the file (teaser cut from the full film)
    var cs = parseFloat(video.getAttribute('data-ff-clip-start'));
    var ce = parseFloat(video.getAttribute('data-ff-clip-end'));
    if (!isNaN(cs) && !isNaN(ce) && ce > cs) {
      var seekStart = function () { try { if (video.currentTime < cs || video.currentTime >= ce) video.currentTime = cs; } catch (err) {} };
      video.addEventListener('loadedmetadata', seekStart);
      video.addEventListener('timeupdate', function () { if (video.currentTime >= ce - 0.05) { video.currentTime = cs; } });
      video.addEventListener('ended', function () { video.currentTime = cs; var p = video.play(); if (p && p.catch) p.catch(function () {}); });
      if (video.readyState >= 1) seekStart();
    }
    if (reduceMotion) { video.removeAttribute('autoplay'); return; }
    var tryPlay = function () {
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked — poster stays */ });
    };
    if (!('IntersectionObserver' in window)) { tryPlay(); return; }
    if (!autoplayObserver) {
      autoplayObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            if (v.dataset.ffUserPaused) return;
            var pl = v.play();
            if (pl && pl.catch) pl.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { threshold: 0.15 });
    }
    autoplayObserver.observe(video);
  }

  /* ---------------------------------------------------------------------- */
  /* Video modal                                                             */
  /* ---------------------------------------------------------------------- */
  var modal = null;
  var lastTrigger = null;

  function parseVideo(url) {
    if (!url) return null;
    var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (yt) return { type: 'youtube', id: yt[1] };
    var vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return { type: 'vimeo', id: vm[1] };
    if (/\.(mp4|webm|mov|m3u8)(\?|$)/i.test(url)) return { type: 'mp4', src: url };
    return null;
  }

  function buildModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'ff-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video');
    modal.innerHTML =
      '<div class="ff-modal__backdrop" data-ff-modal-close></div>' +
      '<div class="ff-modal__dialog">' +
        '<button type="button" class="ff-modal__close no-hover" data-ff-modal-close aria-label="Close video">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        '<div class="ff-modal__player"></div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-ff-modal-close]')) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });
    return modal;
  }

  function openModal(trigger) {
    var url = trigger.getAttribute('data-ff-video-open');
    var info = parseVideo(url);
    if (!info) { if (url) window.open(url, '_blank', 'noopener'); return; }
    lastTrigger = trigger;
    var m = buildModal();
    var player = m.querySelector('.ff-modal__player');
    var html = '';
    if (info.type === 'youtube') {
      html = '<iframe src="https://www.youtube-nocookie.com/embed/' + info.id + '?autoplay=1&rel=0&modestbranding=1&playsinline=1" title="Video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>';
    } else if (info.type === 'vimeo') {
      html = '<iframe src="https://player.vimeo.com/video/' + info.id + '?autoplay=1&title=0&byline=0&portrait=0" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    } else {
      html = '<video src="' + info.src + '" controls autoplay playsinline></video>';
    }
    player.innerHTML = html;
    m.classList.add('is-open');
    document.documentElement.classList.add('ff-modal-open');
    // pause any preview loops in the trigger's section
    var section = trigger.closest('.ff-section');
    if (section) section.querySelectorAll('video[data-ff-autoplay]').forEach(function (v) { v.pause(); });
    setTimeout(function () { var c = m.querySelector('.ff-modal__close'); if (c) c.focus(); }, 50);
    var vid = player.querySelector('video');
    if (vid) { var p = vid.play(); if (p && p.catch) p.catch(function () {}); }
    // true full screen (Fullscreen API on the dialog so the close button stays)
    if (trigger.hasAttribute('data-ff-fullscreen')) {
      var dlg = m.querySelector('.ff-modal__dialog');
      var el = dlg;
      var req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (!req && vid && vid.webkitEnterFullscreen) { try { vid.webkitEnterFullscreen(); } catch (e) {} } // iOS Safari
      else if (req) { try { var fp = req.call(el); if (fp && fp.catch) fp.catch(function () {}); } catch (e) {} }
    }
  }
  function exitFullscreenIfAny() {
    var fs = document.fullscreenElement || document.webkitFullscreenElement;
    if (fs && fs.closest && fs.closest('.ff-modal')) {
      var ex = document.exitFullscreen || document.webkitExitFullscreen;
      if (ex) { try { var p = ex.call(document); if (p && p.catch) p.catch(function () {}); } catch (e) {} }
    }
  }
  ['fullscreenchange', 'webkitfullscreenchange'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      var fs = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fs && modal && modal.classList.contains('is-open')) closeModal();
    });
  });

  function closeModal() {
    if (!modal) return;
    exitFullscreenIfAny();
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('ff-modal-open');
    var player = modal.querySelector('.ff-modal__player');
    setTimeout(function () { player.innerHTML = ''; }, 250);
    if (lastTrigger) {
      var section = lastTrigger.closest('.ff-section');
      if (section) section.querySelectorAll('video[data-ff-autoplay]').forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); });
      lastTrigger.focus();
    }
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ff-video-open]');
    if (!t) return;
    e.preventDefault();
    openModal(t);
  });

  /* ---------------------------------------------------------------------- */
  /* Inline play (watch-video "play in place" mode)                          */
  /* ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ff-inline-play]');
    if (!t) return;
    e.preventDefault();
    var section = t.closest('[data-ff-vwatch]');
    var holder = section ? section.querySelector('[data-ff-inline-player]') : null;
    var url = t.getAttribute('data-ff-inline-play');
    var info = parseVideo(url);
    if (!section || !holder || !info) { t.setAttribute('data-ff-video-open', url); openModal(t); return; }
    var html = '';
    if (info.type === 'youtube') {
      html = '<iframe src="https://www.youtube-nocookie.com/embed/' + info.id + '?autoplay=1&rel=0&modestbranding=1&playsinline=1" title="Video" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>';
    } else if (info.type === 'vimeo') {
      html = '<iframe src="https://player.vimeo.com/video/' + info.id + '?autoplay=1&title=0&byline=0&portrait=0" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    } else {
      html = '<video src="' + info.src + '" controls autoplay playsinline></video><button type="button" class="ff-vwatch__back no-hover" data-ff-inline-close aria-label="Close video"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button>';
    }
    holder.innerHTML = html;
    holder.hidden = false;
    section.classList.add('is-playing');
    section.querySelectorAll('video[data-ff-autoplay]').forEach(function (v) { v.pause(); });
    var vid = holder.querySelector('video');
    if (vid) {
      var p = vid.play(); if (p && p.catch) p.catch(function () {});
      vid.addEventListener('ended', function () { closeInline(section); });
    }
  });
  function closeInline(section) {
    var holder = section.querySelector('[data-ff-inline-player]');
    if (holder) { holder.innerHTML = ''; holder.hidden = true; }
    section.classList.remove('is-playing');
    section.querySelectorAll('video[data-ff-autoplay]').forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); });
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-ff-inline-close]');
    if (!b) return;
    var section = b.closest('[data-ff-vwatch]');
    if (section) closeInline(section);
  });

  /* ---------------------------------------------------------------------- */
  /* Founder letter pop-up: any link to #<anchor> opens the section's       */
  /* <template data-ff-letter-template="anchor"> in a full-screen overlay    */
  /* ---------------------------------------------------------------------- */
  var letterModal = null, letterTrigger = null;
  function letterTemplate(id) { return document.querySelector('template[data-ff-letter-template="' + id + '"]'); }
  function openLetter(id, trigger) {
    var tpl = letterTemplate(id);
    if (!tpl) return false;
    if (!letterModal) {
      letterModal = document.createElement('div');
      letterModal.className = 'ff-letter-modal';
      letterModal.setAttribute('role', 'dialog');
      letterModal.setAttribute('aria-modal', 'true');
      letterModal.innerHTML = '<div class="ff-letter-modal__backdrop" data-ff-letter-close></div><div class="ff-letter-modal__panel" tabindex="-1"><button type="button" class="ff-letter-modal__close no-hover" data-ff-letter-close aria-label="Close letter"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button><div class="ff-letter-modal__content"></div></div>';
      document.body.appendChild(letterModal);
      letterModal.addEventListener('click', function (e) { if (e.target.closest('[data-ff-letter-close]')) closeLetter(); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && letterModal.classList.contains('is-open')) closeLetter(); });
    }
    var content = letterModal.querySelector('.ff-letter-modal__content');
    content.innerHTML = '';
    content.appendChild(tpl.content.cloneNode(true));
    var h = content.querySelector('.ff-h2, h2');
    letterModal.setAttribute('aria-label', h ? h.textContent.trim() : 'Letter');
    letterTrigger = trigger || null;
    letterModal.classList.add('is-open');
    document.documentElement.classList.add('ff-letter-open');
    var panel = letterModal.querySelector('.ff-letter-modal__panel');
    panel.scrollTop = 0;
    setTimeout(function () { panel.focus(); }, 60);
    return true;
  }
  function closeLetter() {
    if (!letterModal) return;
    letterModal.classList.remove('is-open');
    document.documentElement.classList.remove('ff-letter-open');
    if (letterTrigger && letterTrigger.focus) letterTrigger.focus();
    if (location.hash && letterTemplate(location.hash.slice(1))) history.replaceState(null, '', location.pathname + location.search);
  }
  // capture phase so the theme's [data-cc-animate-click] page-veil handler never sees these clicks
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ff-letter-open]');
    if (btn) { e.preventDefault(); e.stopPropagation(); openLetter(btn.getAttribute('data-ff-letter-open'), btn); return; }
    var a = e.target.closest('a[href*="#"]');
    if (!a) return;
    var href = a.getAttribute('href') || '';
    var hash = href.split('#')[1];
    if (!hash) return;
    var path = href.split('#')[0];
    var samePage = !path || path === '/' || path === location.pathname || path === (location.pathname + location.search);
    if (!samePage) return;
    if (letterTemplate(hash)) { e.preventDefault(); e.stopPropagation(); openLetter(hash, a); }
  }, true);
  function openLetterFromHash() {
    var id = (location.hash || '').slice(1);
    if (id && letterTemplate(id)) openLetter(id, null);
  }
  window.addEventListener('hashchange', openLetterFromHash);

  /* ---------------------------------------------------------------------- */
  /* Video chips: mute / play-pause act on the section's visible video       */
  /* ---------------------------------------------------------------------- */
  function visibleVideos(section) {
    return Array.prototype.filter.call(section.querySelectorAll('video'), function (v) {
      return v.offsetParent !== null && getComputedStyle(v).display !== 'none';
    });
  }
  document.addEventListener('click', function (e) {
    var mute = e.target.closest('[data-ff-mute]');
    var pp = e.target.closest('[data-ff-playpause]');
    if (!mute && !pp) return;
    var section = (mute || pp).closest('.ff-section');
    if (!section) return;
    var vids = visibleVideos(section);
    if (mute) {
      var soundOn = !vids.some(function (v) { return !v.muted; });
      vids.forEach(function (v) { v.muted = !soundOn; });
      section.classList.toggle('is-unmuted', soundOn);
      mute.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      mute.setAttribute('aria-label', soundOn ? 'Mute video' : 'Unmute video');
      if (soundOn) vids.forEach(function (v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); });
    }
    if (pp) {
      var anyPlaying = vids.some(function (v) { return !v.paused; });
      vids.forEach(function (v) {
        if (anyPlaying) { v.pause(); v.dataset.ffUserPaused = '1'; }
        else { delete v.dataset.ffUserPaused; var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      });
      section.classList.toggle('is-paused', anyPlaying);
      pp.setAttribute('aria-pressed', anyPlaying ? 'true' : 'false');
      pp.setAttribute('aria-label', anyPlaying ? 'Play video' : 'Pause video');
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Expand / collapse (founder letter)                                      */
  /* ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ff-expand]');
    if (!btn) return;
    e.preventDefault();
    var target = document.getElementById(btn.getAttribute('aria-controls'));
    if (!target) return;
    var open = target.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var more = btn.getAttribute('data-label-more') || btn.textContent;
    var less = btn.getAttribute('data-label-less') || more;
    btn.querySelector('span') ? (btn.querySelector('span').textContent = open ? less : more) : (btn.textContent = open ? less : more);
    if (!open) {
      var top = target.getBoundingClientRect().top + window.pageYOffset - 140;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  });

  /* ---------------------------------------------------------------------- */
  /* One-tap add to cart ([data-ff-atc][data-variant-id])                    */
  /* Mirrors theme.js add_to_cart(): POST cart/add.js, then refresh the      */
  /* liquid-ajax-cart drawer. Success state swaps the icon for ~1.6s.        */
  /* ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ff-atc]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      btn.classList.remove('is-loading');
      btn.classList.add('is-done');
      if ('liquidAjaxCart' in window) {
        window.liquidAjaxCart.cartRequestUpdate();
        document.body.classList.add('js-ajax-cart-open');
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      setTimeout(function () { btn.classList.remove('is-done'); btn.disabled = false; }, 1600);
    }).catch(function () {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    });
  });

  /* ---------------------------------------------------------------------- */
  /* Hero floating card: mark loaded for entrance                            */
  /* ---------------------------------------------------------------------- */
  function initHero(root) {
    (root || document).querySelectorAll('.ff-hero').forEach(function (hero) {
      if (hero.ffInit) return;
      hero.ffInit = true;
      setTimeout(function () { hero.classList.add('is-loaded'); }, 120);
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                    */
  /* ---------------------------------------------------------------------- */
  function init(root) {
    root = root || document;
    initReveals(root);
    initHero(root);
    root.querySelectorAll('[data-ff-scroller]').forEach(initScroller);
    root.querySelectorAll('video[data-ff-autoplay]').forEach(initAutoplay);
    if (root === document) openLetterFromHash();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var slide = e.target && e.target.closest ? e.target.closest('.ff-track > *') : null;
    if (slide && slide.scrollIntoView) slide.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
  });

  window.ffHome = { init: init, openVideo: openModal, closeVideo: closeModal, openLetter: openLetter, closeLetter: closeLetter };
})();
